import {
  compactDiagramVersions,
  stripPhotosFromDocument,
  type DiagramDocument,
  type DiagramVersionRecord,
} from '@ukrop-nm/shared';

export function isQuotaError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' || err.code === 22)
  );
}

export function pruneOtherVersionHistories(keepVersionsKey: string) {
  const remove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes('diagram.versions.') && key !== keepVersionsKey) remove.push(key);
  }
  for (const key of remove) localStorage.removeItem(key);
}

function setJson(key: string, value: unknown) {
  const json = JSON.stringify(value);
  try {
    localStorage.setItem(key, json);
  } catch (err) {
    if (!isQuotaError(err)) throw err;
    localStorage.removeItem(key);
    localStorage.setItem(key, json);
  }
}

/**
 * Write the live diagram (photos kept). Version history is compact (≤3, no photos).
 * On QuotaExceeded: drop other sites' histories, then versions, then photos on the stored live copy.
 * Does not throw quota errors — cloud persist can still overlay the in-memory document.
 */
export function persistDiagramLocal(opts: {
  diagramKey: string;
  versionsKey: string;
  document: DiagramDocument;
  versions: DiagramVersionRecord[];
}): { wroteDiagram: boolean; wroteVersions: boolean } {
  const compactVersions = compactDiagramVersions(opts.versions);
  const attempts: Array<{ document: DiagramDocument; versions: DiagramVersionRecord[] | null }> = [
    { document: opts.document, versions: compactVersions },
    { document: opts.document, versions: compactVersions.slice(0, 1) },
    { document: opts.document, versions: null },
    { document: stripPhotosFromDocument(opts.document), versions: null },
  ];

  let pruned = false;
  for (const attempt of attempts) {
    try {
      setJson(opts.diagramKey, attempt.document);
      if (attempt.versions) {
        setJson(opts.versionsKey, attempt.versions);
      } else {
        localStorage.removeItem(opts.versionsKey);
      }
      return { wroteDiagram: true, wroteVersions: Boolean(attempt.versions) };
    } catch (err) {
      if (!isQuotaError(err)) throw err;
      if (!pruned) {
        pruneOtherVersionHistories(opts.versionsKey);
        pruned = true;
      }
    }
  }
  return { wroteDiagram: false, wroteVersions: false };
}
